import { ReactNode, useEffect, useState, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { storeDeepLink } from "@/utils/deepLinking";
import { useToast } from "@/components/ui/use-toast";

// Define trading-specific routes that require authentication
const tradingProtectedRoutes = [
  '/dashboard',
  '/chat-history',
  '/trading-history',
  '/compliance',
  '/market-analysis',
  '/settings'
];

// Helper to check if a route requires trading auth
const requiresTradingAuth = (path: string) => {
  return tradingProtectedRoutes.some(route => path.startsWith(route));
};

// Helper to check if we're in development
const isDevelopment = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  const protocol = window.location.protocol;
  
  console.log('ProtectedRoute: Environment check', { 
    hostname, 
    port, 
    protocol, 
    href: window.location.href 
  });
  
  return hostname === 'localhost' || 
         hostname === '127.0.0.1' ||
         port === '5173' ||
         port === '3000' ||
         hostname.includes('localhost') ||
         (protocol === 'http:' && (hostname === 'localhost' || hostname === '127.0.0.1'));
};

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasRedirected, setHasRedirected] = useState(false);
  const location = useLocation();
  const { toast } = useToast();
  const mounted = useRef(true);
  const authCheckInProgress = useRef(false);

  console.log('ProtectedRoute: Component rendered', { 
    isAuthenticated, 
    isLoading, 
    hasRedirected,
    pathname: location.pathname,
    isDev: isDevelopment(),
    timestamp: new Date().toISOString()
  });

  // Reset redirect flag when location changes
  useEffect(() => {
    setHasRedirected(false);
  }, [location.pathname]);

  // Authentication check
  useEffect(() => {
    // Prevent multiple concurrent auth checks
    if (authCheckInProgress.current) {
      console.log('ProtectedRoute: Auth check already in progress, skipping');
      return;
    }

    mounted.current = true;
    authCheckInProgress.current = true;
    
    const checkAuth = async () => {
      try {
        console.log("ProtectedRoute: Starting auth check...");
        
        const devMode = isDevelopment();
        console.log("ProtectedRoute: Development mode:", devMode);
        
        // Check for forced development auth
        const forceAuth = localStorage.getItem('stewie-force-auth') === 'true';
        const devModeFlag = localStorage.getItem('stewie-dev-mode') === 'true';
        
        if ((devMode || devModeFlag) && forceAuth) {
          console.log("ProtectedRoute: Force development auth enabled");
          if (mounted.current) {
            setIsAuthenticated(true);
            setIsLoading(false);
          }
          return;
        }
        
        // For development, use a more robust approach
        if (devMode) {
          console.log("ProtectedRoute: Development environment detected");
          
          // In development, be very permissive with authentication
          // Check if there's any indication of authentication
          const hasLocalStorageAuth = localStorage.getItem('supabase.auth.token') !== null;
          const hasAnySupabaseData = Array.from(Array(localStorage.length).keys())
            .map(i => localStorage.key(i))
            .some(key => key?.includes('supabase'));
          
          if (hasLocalStorageAuth || hasAnySupabaseData) {
            console.log("ProtectedRoute: Found auth data in localStorage, assuming authenticated");
            if (mounted.current) {
              setIsAuthenticated(true);
              setIsLoading(false);
            }
            return;
          }
          
          // Try session check with timeout as fallback
          try {
            const authPromise = supabase.auth.getSession();
            const timeoutPromise = new Promise<never>((_, reject) => 
              setTimeout(() => reject(new Error('Dev timeout')), 3000)
            );
            
            const { data: { session }, error } = await Promise.race([authPromise, timeoutPromise]) as any;
            
            if (!error && session?.user) {
              console.log("ProtectedRoute: Valid session confirmed");
              if (mounted.current) {
                setIsAuthenticated(true);
                setIsLoading(false);
              }
            } else {
              console.log("ProtectedRoute: No valid session in development");
              if (mounted.current) {
                setIsAuthenticated(false);
                setIsLoading(false);
              }
            }
          } catch (devError) {
            console.log("ProtectedRoute: Session check failed in development:", devError);
            if (mounted.current) {
              setIsAuthenticated(false);
              setIsLoading(false);
            }
          }
        } else {
          // Production environment - normal auth check
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (mounted.current) {
            if (error) {
              console.error("ProtectedRoute: Production auth error:", error);
              setIsAuthenticated(false);
            } else {
              const isAuth = !!session?.user;
              console.log("ProtectedRoute: Production auth result:", { isAuth, session: !!session });
              setIsAuthenticated(isAuth);
            }
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error("ProtectedRoute: Auth check failed:", error);
        if (mounted.current) {
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      } finally {
        authCheckInProgress.current = false;
      }
    };
    
    checkAuth();
    
    // Set up auth subscription with improved error handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("ProtectedRoute: Auth state changed:", event, {
          hasSession: !!session,
          userId: session?.user?.id,
          email: session?.user?.email
        });
        
        if (mounted.current) {
          const isAuth = !!session?.user;
          setIsAuthenticated(isAuth);
          setIsLoading(false);
          
          // Reset redirect flag on successful auth
          if (isAuth && hasRedirected) {
            setHasRedirected(false);
          }
        }
      }
    );
    
    return () => { 
      mounted.current = false;
      authCheckInProgress.current = false;
      subscription.unsubscribe();
    };
  }, [location.pathname]); // Re-run when location changes

  console.log('ProtectedRoute: Current state', { 
    isLoading, 
    isAuthenticated, 
    hasRedirected,
    shouldShowLoading: isLoading || isAuthenticated === null,
    pathname: location.pathname,
    timestamp: new Date().toISOString()
  });

  // Show loading state
  if (isLoading || isAuthenticated === null) {
    console.log('ProtectedRoute: Showing loading spinner');
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <div className="text-white text-sm">Checking authentication...</div>
          {isDevelopment() && (
            <div className="text-gray-400 text-xs">Development mode</div>
          )}
        </div>
      </div>
    );
  }

  // If not authenticated and route requires authentication, redirect to login
  if (isAuthenticated === false && requiresTradingAuth(location.pathname) && !hasRedirected) {
    console.log("ProtectedRoute: Not authenticated, redirecting to auth page");
    setHasRedirected(true);
    storeDeepLink(location.pathname + location.search);
    
    setTimeout(() => {
      toast({
        title: "Authentication Required",
        description: "Please sign in to access this page.",
        variant: "destructive",
      });
    }, 0);
    
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // User is authenticated
  console.log('ProtectedRoute: Rendering children');
  return <>{children}</>;
};
